import { useState } from "react";
import { Check } from "lucide-react";
import axios from "axios";
import { Reveal } from "../components/Reveal";
import { SampleTag } from "../components/SampleTag";
import { EXAMS, SLOTS } from "../data/sampleData";

const EMPTY = { name: "", phone: "", exam: EXAMS[0].shortName, slot: SLOTS[0] };

const Book = () => {
    const [form, setForm] = useState(EMPTY);
    const [submitted, setSubmitted] = useState(null);
    const [saveError, setSaveError] = useState(false);
    const [saving, setSaving] = useState(false);

    const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

    const onSubmit = async (e) => {
        e.preventDefault();
        setSaveError(false);
        setSaving(true);
        try {
            await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/bookings`, {
                name: form.name,
                phone: form.phone,
                exam: form.exam,
                slot: form.slot,
                source: "Book form",
            });
            setSubmitted({ ...form });
        } catch (err) {
            setSaveError(true);
        } finally {
            setSaving(false);
        }
    };

    return (
        <main className="py-20 md:py-28" data-testid="book-page">
            <div className="wrap grid gap-14 lg:grid-cols-12 lg:gap-10">
                <Reveal className="lg:col-span-5">
                    <p className="eyebrow">Book</p>
                    <h2 className="mt-4 text-3xl leading-tight tracking-tight sm:text-4xl lg:text-5xl">
                        Reserve the <em className="italic text-brass">empty chair.</em>
                    </h2>
                    <p className="mt-6 max-w-md text-base leading-relaxed text-ink-muted">
                        Tell us who is coming and when. We confirm every slot on
                        WhatsApp within a working day, along with your panel and
                        joining instructions.
                    </p>
                    <div className="mt-8 flex items-center gap-3">
                        <SampleTag />
                        <p className="text-xs text-ink-muted">
                            Booking requests are saved for the owner and scored by
                            Jev; details are sample content.
                        </p>
                    </div>
                </Reveal>

                <Reveal delay={0.1} className="lg:col-span-7">
                    {submitted ? (
                        <div
                            data-testid="book-confirmation"
                            className="border border-brass bg-brass-soft p-8 sm:p-10"
                        >
                            <div className="flex items-center gap-4">
                                <span className="flex h-10 w-10 items-center justify-center border border-brass text-brass">
                                    <Check size={18} strokeWidth={1.5} />
                                </span>
                                <h3 className="font-display text-2xl tracking-tight sm:text-3xl">
                                    Request received.
                                </h3>
                            </div>
                            <p className="mt-6 max-w-md text-base leading-relaxed text-ink">
                                Thank you, {submitted.name}. We&apos;ll confirm your{" "}
                                {submitted.exam} mock board for {submitted.slot} on
                                WhatsApp at {submitted.phone}.
                            </p>
                            <p className="mt-4 text-[11px] font-medium uppercase tracking-[0.18em] text-brass">
                                Confirmation is a sample — the owner confirms every slot personally
                            </p>
                            <button
                                type="button"
                                data-testid="book-reset-btn"
                                onClick={() => {
                                    setForm(EMPTY);
                                    setSubmitted(null);
                                }}
                                className="btn-ghost mt-8"
                            >
                                Make another request
                            </button>
                        </div>
                    ) : (
                        <form
                            onSubmit={onSubmit}
                            data-testid="book-form"
                            className="border border-line bg-surface p-8 sm:p-10"
                        >
                            <div className="grid gap-8 sm:grid-cols-2">
                                <div className="sm:col-span-2">
                                    <label htmlFor="book-name" className="field-label">
                                        Full name
                                    </label>
                                    <input
                                        id="book-name"
                                        type="text"
                                        required
                                        value={form.name}
                                        onChange={set("name")}
                                        placeholder="Your name"
                                        data-testid="book-name-input"
                                        className="field"
                                    />
                                </div>

                                <div className="sm:col-span-2">
                                    <label htmlFor="book-phone" className="field-label">
                                        WhatsApp number
                                    </label>
                                    <input
                                        id="book-phone"
                                        type="tel"
                                        required
                                        pattern="[0-9+ \-]{8,15}"
                                        value={form.phone}
                                        onChange={set("phone")}
                                        placeholder="+91 ·····"
                                        data-testid="book-phone-input"
                                        className="field"
                                    />
                                </div>

                                <div>
                                    <label htmlFor="book-exam" className="field-label">
                                        Exam
                                    </label>
                                    <div className="relative">
                                        <select
                                            id="book-exam"
                                            value={form.exam}
                                            onChange={set("exam")}
                                            data-testid="book-exam-select"
                                            className="field appearance-none pr-8"
                                        >
                                            {EXAMS.map((e) => (
                                                <option key={e.id} value={e.shortName}>
                                                    {e.shortName}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                </div>

                                <div>
                                    <label htmlFor="book-slot" className="field-label">
                                        Preferred slot
                                    </label>
                                    <div className="relative">
                                        <select
                                            id="book-slot"
                                            value={form.slot}
                                            onChange={set("slot")}
                                            data-testid="book-slot-select"
                                            className="field appearance-none pr-8"
                                        >
                                            {SLOTS.map((s) => (
                                                <option key={s} value={s}>
                                                    {s}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                </div>
                            </div>

                            {saveError && (
                                <p
                                    data-testid="book-save-error"
                                    className="mt-6 border border-red-700/40 bg-red-700/10 px-4 py-3 text-sm leading-relaxed text-red-700 dark:text-red-400"
                                >
                                    We couldn&apos;t save your request just now. Please try
                                    again — or WhatsApp us directly at +91 87889 24082 and
                                    we&apos;ll book you in.
                                </p>
                            )}

                            <button
                                type="submit"
                                disabled={saving}
                                data-testid="book-submit-btn"
                                className="btn-primary mt-10 w-full sm:w-auto disabled:opacity-60"
                            >
                                {saving ? "Sending…" : "Request booking"}
                            </button>
                        </form>
                    )}
                </Reveal>
            </div>
        </main>
    );
};

export default Book;
